import type { PickingInfo } from '@babylonjs/core/Collisions/pickingInfo';
import type { KeyboardInfo } from '@babylonjs/core/Events/keyboardEvents';
import type { PointerInfoBase } from '@babylonjs/core/Events/pointerEvents';
import type { BoundingBoxInfo } from '@kb3d/view/modules';
import type { eConnectionMode, eDragMode, eMateType } from './enums';
import type { Connector } from './features/connector';
import type { MateNode } from './mates/mate-node';
import type { SpaceNode } from './meshes/space-node';
import type { KbVector } from './primitives';

export enum eHandler {
    click = 'click',
    doubleClick = 'doubleClick',
    drag = 'drag',
    draggable = 'draggable',
    hotspot = 'hotspot',
    boundingBox = 'boundingBox',
}

export interface ICameraAnimationOptions {
    duration: number;
    frame?: boolean;
    frameOptions?: Partial<IFramingOptions>;
    frameTarget?: SpaceNode;
}

export interface IFramingOptions {
    rotate: boolean;
    pan: boolean;
    zoom: boolean;
    padding: number;
}

export enum eCameraOrientation {
    perspective = 'perspective',
    front = 'front',
    left = 'left',
    back = 'back',
    right = 'right',
    top = 'top',
    bottom = 'bottom',
}

export interface IKb3dPickInfo {
    pickInfo: PickingInfo;

    /** the specific node that the pointer is over */
    targetNode?: SpaceNode;

    /** the 3d point in the world where the mesh was clicked */
    pickedPoint?: KbVector;
    /** the 3d point of the target mesh that was clicked */
    meshPoint?: KbVector;

    /** UV intersection coordinates */
    u?: number;
    v?: number;

    getNormal: () => KbVector | undefined;
}

export interface IKb3dPointerArgs extends PointerInfoBase, IKb3dPickInfo {
    /** the node that the handler was set on (could be an ancestor of the targetNode) */
    sourceNode?: SpaceNode;
    /** whether the event should continue to bubble up the ancestor nodes */
    propagate?: boolean;
    /** the type of pointer device.  Eg:  'mouse', 'touch', etc. */
    pointerType?: string;
    /** the mouse button that was pressed (if there is one).  0 = left, 1 = center, 2 = right */
    button?: number;
    /** whether the ctrl key was pressed during the click */
    ctrlKey?: boolean;
    /** whether the shift key was pressed during the click */
    shiftKey?: boolean;
    /** whether the alt key was pressed during the click */
    altKey?: boolean;
    /** the screen coordinates of the click in relation to the canvas viewer */
    canvasX?: number;
    canvasY?: number;
}

export interface IKb3dFacetSelectArgs extends IKb3dPointerArgs {
    selectedFaces: number[];
}

export interface IKeyboardArgs extends KeyboardInfo {
    key: string;
    keyCode: number;
    altKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    repeat: boolean;
}

export type Kb3dPointerHandler = (args: IKb3dPointerArgs) => void;

export type Kb3dDragHandler = (args: IDragArgs) => void;

export type Kb3dBoundingBoxChangeHandler = {
    mode: 'once' | 'forever';
    handler: (args: BoundingBoxInfo) => void;
};

export interface IDragArgs {
    dragNode: SpaceNode;
    newPos: KbVector;
    startPos: KbVector;
    newRotationEuler: KbVector;
    newRotationAxis: KbVector;
    newRotationAngle: number;
    allow: boolean;
    u: number;
    v: number;
    //relativeSurfacePos: IVector2;

    /*following only used for connections*/
    connection?: IConnection;

    /** if withMates is true for the draggable, then this will contain the added mate in the dropped rule */
    mate?: MateNode;
}

export interface IConnection {
    dragConnector: Connector;
    otherConnector: Connector;
    dragNode?: SpaceNode;
    otherNode?: SpaceNode;
}

export interface ICanConnectArgs extends IConnection {
    allow: boolean;
}

// export enum eDragMode {
//     free,
//     plane,
//     //axis,
//     surface
// }

// export enum eConnectorMode {
//     none,
//     world,
//     screen
// }

export interface IDraggable {
    /** The id, name, or wildcard of the node to define as draggable */
    id?: string | SpaceNode;
    /** The mode of the drag */
    mode?: eDragMode; // free, plane, surface
    /** In plane or linear mode, is a vector normal to the drag plane.  This normal + the picked point on the mesh will determine the intersection plane */
    planeNormal?: KbVector;
    // /** In plane mode, it is the origin of the plane.  If not provided, the origin of the drag node is used (usually the desired effect) */
    // planeOrigin?: KbVector;
    /** if disabled, the node origin will snap to the mouse position when you start dragging */
    disableOffset?: boolean;

    //axis?: KbVector;

    /** In surface mode, holds the id (if a singular surface) or ids (if multiple surfaces) of the surfaces */
    surfaceNodes?: string | string[];

    /** whether connectors are enabled during this drag operation and if so how the gauge proximity */
    connectionMode?: eConnectionMode;
    /** how to align the connectors when a connection is made */
    connectionType?: eMateType;
    /** whether to show the connector spheres or not */
    showConnectors?: boolean;
    /** the proximity distance between connectors that counts as a possible connection */
    connectRadius?: number;
    /** when a connector drag, controls the opacity of the dragged items */
    dragOpacity?: number;
    /**
     * Will add mates once a connection is made.  Also uses mates to understand what connectors
     * are open for connection (meaning not part of mate).  Will dynamically swap mate connectors
     * based on the drag node.  Only valid if connectionMode is not 'none'
     * */
    withMates?: boolean;

    /** Called when the drag is started */
    dragStart?: (args: IDragArgs) => void;
    // /** Called at every frame to see if we want to allow the move */
    // allow?: (args: IDragArgs) => boolean;
    /** Called at every frame while dragging.  Can be used to custom update the position by modifying newPos */
    dragging?: (args: IDragArgs) => void;
    /** Called when the node is dropped */
    dropped?: (args: IDragArgs) => void;

    canConnect?: (args: ICanConnectArgs) => void;
}
